import { createContext, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { useLoginErrorCode } from '@/bo-kit/hooks/useLoginErrorCode';
import { useReturnUrl } from '@/bo-kit/hooks/useReturnUrl';
import { LoginAndResetPassErrorCode } from '../../../../../../../transport/types/LoginAndResetPassErrorCode';
import { ResetPasswordRequest } from '../../../../../../../transport/types/ResetPasswordRequest';

type UseDoResetPasswordMutation = [
    (data: ResetPasswordRequest) => void,
    {
        isSuccess: boolean;
        isLoading: boolean;
        error?: FetchBaseQueryError | SerializedError;
        isError: boolean;
    }
];

export const UseDoResetPasswordMutationContext = createContext<
    UseDoResetPasswordMutation | undefined
>(undefined);

export const useDoVerifyResetPassword = () => {
    const navigate = useNavigate();
    const contextValue = useContext(UseDoResetPasswordMutationContext);
    if (!contextValue) {
        throw new Error(
            "No useDoVerifyResetPassword defined with react context, pls implement: '<UseDoResetPasswordMutationContext.Provider value={[doResetPassword, state]}>{children}</UseDoResetPasswordMutationContext.Provider>'"
        );
    }

    const [doVerify, state] = contextValue;

    const { errorCode } = useLoginErrorCode(state);

    const [returnUrl] = useReturnUrl();

    const errorMessage = useMemo(() => {
        if (!state.isError) {
            return null;
        }
        if (errorCode === LoginAndResetPassErrorCode.INVALIDACTIVATECODE) {
            return 'Wrong code';
        } else if (errorCode === LoginAndResetPassErrorCode.ALREADYACTIVATED) {
            return 'User already activated, try to log in';
        }
        if (!state.error || !('error' in state.error)) {
            return 'An unexpected error occurred';
        }
        return state.error.error;
    }, [errorCode, state.error, state.isError]);

    useEffect(() => {
        if (state.isSuccess) {
            navigate('success', { state: { returnUrl } });
        }
    }, [state, navigate, returnUrl]);

    return {
        doVerify,
        ...state,
        errorMessage,
    };
};
